import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment/moment";
// @ts-expect-error pure js
import RadioStandalone from "../UI/RadioStandalone";
// @ts-expect-error pure js
import RadioGroup from "../UI/RadioGroup";
// @ts-expect-error pure js
import PaymentMethod from "./PaymentMethod";
import AddressFacturation from "../Account/AddressFacturation";
// @ts-expect-error pure js
import CGVCheckbox from "./CGVCheckbox";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
// @ts-expect-error pure js
import useStripePaymentMethods from "../../hooks/use-stripe-payment-methods";
// @ts-expect-error pure js
import useStripePayment from "../../hooks/use-stripe-payment.js";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError, TextOneOrMany } from "../../utils";
// @ts-expect-error pure js
import { TypeAbonnement } from "../../utils/constants/account";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
import { conversionData, trackEvent } from "../../utils/tracking";
import { trackMetaEvent } from "../../utils/metaPixel";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import Text from "../UI/Text";
import styles from "./SubscriptionCheckout.module.css";

interface SubscriptionCheckoutProps {
  priceId: string;
  nbBiens: number;
  isAnnual: boolean;
  pricePerPeriod: number;
  onBack: () => void;
  onSubscribed: () => void;
}

export default function SubscriptionCheckout({
  priceId,
  nbBiens,
  isAnnual,
  pricePerPeriod,
  onBack,
  onSubscribed,
}: SubscriptionCheckoutProps) {
  const location = useLocation();
  const { account, hasValidAddress, subscribe } = useAccount();
  const {
    paymentMethods,
    refresh: refreshPaymentMethods,
    defaultPaymentMethod,
  } = useStripePaymentMethods();
  const { initiateSetup } = useStripePayment();

  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod?.id);
  const [codePromo, setCodePromo] = useState("");
  const [codePromoInput, setCodePromoInput] = useState("");
  const [codePromoPreview, setCodePromoPreview] = useState<{
    code?: string;
    valid?: boolean;
    days?: number;
  }>();
  const [paymentDate, setPaymentDate] = useState<string | null>(null);
  const [trialDays, setTrialDays] = useState(0);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [montantReduit, setMontantReduit] = useState<number | null>(null);
  const [message, setMessage] = useState<ReactNode>("");
  const [cgv, setCgv] = useState(false);
  const [previewingPayment, previewPayment] = useLoader(
    useCallback((values: object) => subscribe(values, true), [subscribe]),
  );

  const canHavePromoCode =
    [TypeAbonnement.AUCUN, TypeAbonnement.TRIAL].includes(
      account.type_abonnement,
    ) && !account.promo_abonnement;

  // Code d'un lien partenaire mémorisé à l'inscription : pré-rempli, mais modifiable
  useEffect(() => {
    if (account.code_promo_inscription) {
      setCodePromo((current) => current || account.code_promo_inscription);
      setCodePromoInput((current) => current || account.code_promo_inscription);
    }
  }, [account.code_promo_inscription]);

  useEffect(() => {
    if (paymentMethods.length === 1) {
      setPaymentMethod(paymentMethods[0].id);
    } else if (defaultPaymentMethod) {
      setPaymentMethod(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod, paymentMethods]);

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      try {
        const res = await previewPayment({
          preview: true,
          stripe_price_abonnement: priceId,
          nb_biens: nbBiens,
          code_promo: codePromo,
        });
        if (cancelled) return;
        const date = moment(res.data.payment_date);
        setPreviewFailed(false);
        setPaymentDate(date.isValid() ? date.format("DD/MM/YYYY") : null);
        setTrialDays(res.data.trial ?? 0);
        setMontantReduit(res.data.montant_reduit);
        setCodePromoPreview(res.data.promo);
      } catch (e) {
        console.error(e);
        if (!cancelled) setPreviewFailed(true);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [codePromo, nbBiens, previewPayment, priceId]);

  const [isSubmitting, submitHandler] = useLoader(async () => {
    setMessage("");

    if (!paymentMethod) {
      setMessage(
        "Veuillez renseigner un moyen de paiement pour les paiements récurrents.",
      );
      return;
    }
    if (!hasValidAddress) {
      setMessage("Veuillez renseigner une adresse de facturation valide.");
      return;
    }
    if (!cgv) {
      setMessage("Veuillez accepter les conditions générales de vente.");
      return;
    }

    try {
      const result = await subscribe({
        payment_method_id: paymentMethod,
        stripe_price_abonnement: priceId,
        nb_biens: nbBiens,
        code_promo: codePromo,
      });
      const conversion = conversionData(result);
      const amount = {
        value: conversion.value ?? 0,
        currency: conversion.currency ?? "EUR",
      };
      trackMetaEvent("Subscribe", conversion.transaction_id, amount);
      trackMetaEvent("Purchase", conversion.transaction_id, amount);
      trackEvent("subscribed", location, {
        ...conversion,
        user_data: { email: account.mail },
      });
      onSubscribed();
    } catch (e) {
      setMessage(handleAPIError(e));
    }
  });

  return (
    <div className={styles.checkout}>
      <Card className={styles.recap} padding="sm">
        <div className={styles.recapHeader}>
          <div>
            <Text weight="bold" as="span">
              Formule {isAnnual ? "annuelle" : "mensuelle"}
            </Text>{" "}
            <Text as="span">
              ·{" "}
              <TextOneOrMany
                one={`${nbBiens} bien`}
                many={`${nbBiens} biens`}
                number={nbBiens}
              />{" "}
              · {currencyFormatter(pricePerPeriod)} / {isAnnual ? "an" : "mois"}
            </Text>
          </div>
          <Button variant="tertiary" onClick={onBack}>
            Modifier
          </Button>
        </div>
        {previewingPayment ? (
          <Spinner width="2em" />
        ) : previewFailed ? (
          <Text variant="deci" color="danger">
            Impossible de calculer le montant du prélèvement. Rechargez la page
            ou contactez-nous sur le chat.
          </Text>
        ) : (
          montantReduit !== null && (
            <div>
              <Text as="span">
                {paymentDate ? (
                  <>
                    {currencyFormatter(montantReduit)} prélevé le {paymentDate}
                    {trialDays > 0 &&
                      `, à la fin de vos ${trialDays} jours d'essai gratuit`}
                  </>
                ) : (
                  "à prélever"
                )}
              </Text>
            </div>
          )
        )}
      </Card>

      <Card padding="sm">
        <Text weight="bold" className={styles.blockTitle}>
          Moyen de paiement
        </Text>
        {trialDays > 0 && (
          <Text>
            Aucun prélèvement avant la fin de vos {trialDays} jours d'essai.
          </Text>
        )}
        <RadioGroup className={"gridColWrap300 marginT10"} styling={"button"}>
          {paymentMethods.map((method: { id: string }, i: number) => (
            <RadioStandalone
              name="payment_method"
              onChange={() => setPaymentMethod(method.id)}
              checked={paymentMethod === method.id}
              key={i}
              label={<PaymentMethod method={method} flat />}
            />
          ))}
        </RadioGroup>
        <Button
          type="button"
          variant="tertiary"
          className={"center marginT10"}
          onClick={() =>
            initiateSetup({
              onSetupSuccessful: refreshPaymentMethods,
            })
          }
        >
          Ajouter un moyen de paiement
        </Button>
      </Card>

      <AddressFacturation />

      {canHavePromoCode && (
        <div>
          <TextInputStandalone
            label={"Code promo"}
            placeholder={"ABC123"}
            value={codePromoInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCodePromoInput(e.target.value)
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              setCodePromo(e.target.value)
            }
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                setCodePromo((e.target as HTMLInputElement).value);
                e.preventDefault();
              }
            }}
          />
          {codePromoPreview?.code && (
            <Text variant="deci" color="muted">
              Code '{codePromoPreview.code}' :{" "}
              {codePromoPreview.valid
                ? `${codePromoPreview.days} jours d'essai additionnels`
                : "invalide"}
            </Text>
          )}
        </div>
      )}

      <CGVCheckbox
        checked={cgv}
        onChange={() => setCgv((prev: boolean) => !prev)}
        remboursable
      />

      <Callout variant="danger">{message}</Callout>

      <Button
        onClick={submitHandler}
        isLoading={isSubmitting}
        disabled={previewingPayment}
      >
        {trialDays > 0
          ? `Commencer mes ${trialDays} jours gratuits`
          : "Confirmer mon abonnement"}
      </Button>
    </div>
  );
}
